/***
 *
 * Dynamic List Block
 *
 * An element within a collection/search page that can auto-inject itself at a specified index in the product/result grid. Also known as "Curated Items".
 * e.g. A client wants to add a card within the search/collection page results to advertise a specific promotion
 *
 * @param {number} (required) data-mobile-index - What element index the curated item (DynamicListBlock) will inject itself in @ mobile breakpoints
 * @param {number} (required) data-desktop-index - What element index the curated item (DynamicListBlock) will inject itself in @ desktop breakpoints
 * @param {string} (required) data-wrapper
 * The ID of the element which is the direct parent of the curated item (DynamicListBlock)/grid elements. This is the wrapper we query to inject into.
 * This will automatically search for a matching ID or class - in that order.
 * e.g. data-wrapper="wrapperElement" will first query this.closest(`#${this.wrapperId}`); and if it finds no matches will query this.closest(`.${this.wrapperID}`);
 *
 */

if (!customElements.get('dynamic-list-block')) {
    customElements.define(
        'dynamic-list-block',

        class DynamicListBlock extends HTMLElement {
            constructor() {
                super();
                this.onResize = debounce(() => {
                    this.inject();
                }, 50).bind(this);
                this.connected = false;
            }

            connectedCallback() {
                this.mobileIndex = this.getAttribute('data-mobile-index')
                    ? parseInt(this.getAttribute('data-mobile-index'), 10)
                    : null;
                this.desktopIndex = this.getAttribute('data-desktop-index')
                    ? parseInt(this.getAttribute('data-desktop-index'), 10)
                    : null;
                this.wrapperID = this.getAttribute('data-wrapper');

                if (!this.mobileIndex || !this.desktopIndex || !this.wrapperID) return;

                this.desktopIndex = this.desktopIndex - 1;
                this.mobileIndex = this.mobileIndex - 1;

                this.wrapper = this.closest(`#${this.wrapperId}`);
                if (!this.wrapper) this.wrapper = this.closest(`.${this.wrapperID}`);
                if (!this.wrapper) return;

                this.currentMediaQuery = p80.mediaQueryService.currentMediaQuery;

                this.bind();
                if (!this.connected) this.inject();
            }

            disconnectedCallback() {
                if (this.mediaQueryChangeUnsubscriber) {
                    this.mediaQueryChangeUnsubscriber();
                }
                window.removeEventListener('resize', this.onResize);
            }

            bind() {
                this.mediaQueryChangeUnsubscriber = subscribe(
                    PUB_SUB_EVENTS.mediaQueryChange,
                    (event) => {
                        this.onMediaQueryChange(event);
                    }
                );
                window.addEventListener('resize', this.onResize);
            }

            onMediaQueryChange(event) {
                if (!event || !event.currentMedia) return;
                this.currentMediaQuery = event.currentMedia;
            }

            inject() {
                this.connected = true;
                const injectIndex =
                    this.currentMediaQuery.type === p80.mediaQueryService.mediaTypes.large
                        ? this.desktopIndex
                        : this.mobileIndex;
                const offsetIndex =
                    Array.from(this.wrapper.children).indexOf(this) < injectIndex
                        ? injectIndex + 1
                        : injectIndex;

                if (!this.wrapper.children[offsetIndex]) {
                    this.classList.add('hidden');
                    return;
                }

                if (this.wrapper.children[offsetIndex] === this) return;

                this.classList.remove('hidden');

                this.wrapper.insertBefore(this, this.wrapper.children[offsetIndex]);
            }
        }
    );
}
